// src/app/pages/dashboard/admin_apps/InnerLinkMapPage.tsx
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';
import { Dialog } from 'primereact/dialog';
import { API_URL } from '../../../app/modules/auth/core/_requests';
import { useAuth } from '../../../app/modules/auth';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primereact/resources/themes/mdc-dark-deeppurple/theme.css';

// Updated interface: keywords can be either an array of strings or a string (Postgres array literal)
interface InnerLink {
  id: number;
  shopify_name: string;
  keywords: string[] | string;
  url: string;
  title: string;
}

export const InnerLinkMapPage: FC = () => {
  const toast = useRef<Toast>(null);
  const { currentUser } = useAuth();
  const currentStoreName =
    currentUser?.current_shopify_store || currentUser?.decoded?.current_shopify_store || '';

  // Table state
  const [innerLinks, setInnerLinks] = useState<InnerLink[]>([]);
  const [loading, setLoading] = useState(false);

  // Filters for each column
  const [filters, setFilters] = useState<any>({
    url: { value: null, matchMode: FilterMatchMode.CONTAINS },
    title: { value: null, matchMode: FilterMatchMode.CONTAINS },
    keywords: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  // Dialog & new row state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newRow, setNewRow] = useState<Omit<InnerLink, 'id' | 'shopify_name'>>({
    url: '',
    title: '',
    keywords: [],
  });
  const [newKeywordsInput, setNewKeywordsInput] = useState(''); // for comma-separated keywords input

  // Fetch all inner link rows for the current shop
  const fetchInnerLinks = async () => {
    setLoading(true);
    try {
      const resp = await axios.get<InnerLink[]>(
        `${API_URL}/api/innerlink-map?storeName=${encodeURIComponent(currentStoreName)}`
      );
      setInnerLinks(resp.data || []);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch inner link rows',
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (currentStoreName) {
      fetchInnerLinks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoreName]);

  // Inline cell update (PUT)
  const saveCell = async (rowData: InnerLink) => {
    setLoading(true);
    try {
      // For keywords, send a comma-separated string that the backend will convert
      const payload = {
        storeName: currentStoreName,
        url: rowData.url,
        title: rowData.title,
        keywords:
          typeof rowData.keywords === 'string'
            ? rowData.keywords
            : rowData.keywords.join(', '),
      };
      await axios.put(`${API_URL}/api/innerlink-map/${rowData.id}`, payload);
      toast.current?.show({ severity: 'success', summary: 'Updated', detail: 'Cell updated' });
      await fetchInnerLinks();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to update cell',
      });
    }
    setLoading(false);
  };

  // Add a new inner link row (POST)
  const addNewInnerLink = async () => {
    setLoading(true);
    try {
      const payload = {
        storeName: currentStoreName,
        url: newRow.url,
        title: newRow.title,
        keywords: newKeywordsInput,
      };
      await axios.post(`${API_URL}/api/innerlink-map`, payload);
      toast.current?.show({
        severity: 'success',
        summary: 'Created',
        detail: 'New inner link row added',
      });
      setNewRow({ url: '', title: '', keywords: [] });
      setNewKeywordsInput('');
      setShowAddDialog(false);
      fetchInnerLinks();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to create inner link row',
      });
    }
    setLoading(false);
  };

  // Confirm and delete row
  const confirmDelete = (event: React.MouseEvent<HTMLButtonElement>, rowData: InnerLink) => {
    confirmPopup({
      target: event.currentTarget,
      message: `Delete inner link with title "${rowData.title}"?`,
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => deleteInnerLink(rowData),
    });
  };

  const deleteInnerLink = async (row: InnerLink) => {
    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/api/innerlink-map/${row.id}?storeName=${encodeURIComponent(currentStoreName)}`
      );
      setInnerLinks((prev) => prev.filter((r) => r.id !== row.id));
      toast.current?.show({
        severity: 'info',
        summary: 'Deleted',
        detail: `Removed inner link row with title "${row.title}"`,
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete inner link row',
      });
    }
    setLoading(false);
  };

  // PrimeReact cell editor for inline text
  const textEditor = (options: any) => {
    let val = options.rowData[options.field];
    if (options.field === 'keywords') {
      if (typeof val === 'string') {
        // If value looks like a Postgres array literal, e.g. {vape pen,vape pens}
        if (val.startsWith('{') && val.endsWith('}')) {
          const arr = val.substring(1, val.length - 1).split(',');
          val = arr.join(', ');
        } else {
          // Otherwise, replace commas with comma+space
          val = val.replace(/,/g, ', ');
        }
      } else if (Array.isArray(val)) {
        val = val.join(', ');
      }
    }
    const handleBlur = async () => {
      if (options.field === 'keywords') {
        if (typeof options.rowData[options.field] === 'string') {
          options.rowData[options.field] = options.rowData[options.field]
            .split(',')
            .map((k: string) => k.trim())
            .filter((k: string) => k !== '');
        }
      }
      setInnerLinks([...innerLinks]);
      await saveCell(options.rowData);
    };
    return (
      <InputText
        value={val}
        onChange={(e) => {
          options.rowData[options.field] = e.target.value;
          setInnerLinks([...innerLinks]);
        }}
        onBlur={handleBlur}
      />
    );
  };

  // Custom body template for the keywords column to display comma+space separated values
  const keywordsBodyTemplate = (rowData: InnerLink) => {
    const { keywords } = rowData;
    if (Array.isArray(keywords)) {
      return keywords.join(', ');
    } else if (typeof keywords === 'string') {
      if (keywords.startsWith('{') && keywords.endsWith('}')) {
        const arr = keywords.substring(1, keywords.length - 1).split(',');
        return arr.join(', ');
      } else {
        return keywords.replace(/,/g, ', ');
      }
    }
    return '';
  };

  // Dialog footer for adding new row
  const addDialogFooter = (
    <div className='d-flex justify-content-end'>
      <Button label='Cancel' className='p-button-text me-2' onClick={() => setShowAddDialog(false)} />
      <Button label='Save' className='p-button-primary' onClick={addNewInnerLink} />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <ConfirmPopup />

      <div className='container mt-4'>
        <div className='row mb-3'>
          <div className='col'>
            <h3>Inner Link Map</h3>
          </div>
          <div className='col-auto'>
            <Button
              label='Add New Inner Link'
              icon='pi pi-plus'
              className='p-button-success'
              onClick={() => setShowAddDialog(true)}
            />
          </div>
        </div>

        <DataTable
          value={innerLinks}
          dataKey='id'
          editMode='cell'
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          filters={filters}
          filterDisplay='row'
          loading={loading}
          responsiveLayout='scroll'
          sortMode='multiple'
        >
          <Column
            field='url'
            header='URL'
            editor={textEditor}
            filter
            filterPlaceholder='Search URL'
            showFilterMenu={false}
            sortable
            style={{ width: '250px' }}
          />
          <Column
            field='title'
            header='Title'
            editor={textEditor}
            filter
            filterPlaceholder='Search title'
            showFilterMenu={false}
            sortable
            style={{ width: '200px' }}
          />
          <Column
            field='keywords'
            header='Keywords'
            body={keywordsBodyTemplate}
            editor={textEditor}
            filter
            filterPlaceholder='Search keywords'
            showFilterMenu={false}
            sortable
            style={{ width: '250px' }}
          />
          <Column
            body={(rowData) => (
              <Button
                icon='pi pi-trash'
                className='p-button-text p-button-danger p-button-sm'
                onClick={(e) => confirmDelete(e, rowData)}
              />
            )}
            style={{ width: '70px' }}
          />
        </DataTable>
      </div>

      <Dialog
        visible={showAddDialog}
        header='Add New Inner Link'
        onHide={() => setShowAddDialog(false)}
        style={{ width: '400px' }}
        footer={addDialogFooter}
      >
        <div className='row g-3'>
          <div className='col-12'>
            <label className='form-label'>URL</label>
            <InputText
              value={newRow.url}
              onChange={(e) => setNewRow({ ...newRow, url: e.target.value })}
              className='w-100'
            />
          </div>
          <div className='col-12'>
            <label className='form-label'>Title</label>
            <InputText
              value={newRow.title}
              onChange={(e) => setNewRow({ ...newRow, title: e.target.value })}
              className='w-100'
            />
          </div>
          <div className='col-12'>
            <label className='form-label'>Keywords (comma-separated)</label>
            <InputText
              value={newKeywordsInput}
              onChange={(e) => setNewKeywordsInput(e.target.value)}
              className='w-100'
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export const InnerLinkMapWrapper: FC = () => {
  return (
    <>
      <h1 className='mt-3 ms-3'>Inner Link Map</h1>
      <InnerLinkMapPage />
    </>
  );
};
